import React, { useEffect, useMemo, useState } from 'react';
import { ListSkeleton } from '../../../components/UI/Skeleton/Skeleton';
import moment from 'moment';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSelector } from 'react-redux';

import { useIsLoggedInQuery } from '../../../redux/apis/authApi';
import { useGetDashboardChartQuery } from '../../../redux/apis/Dashboard';
import { selectDisplayTimezone } from '../../../redux/utils/apiLocality';
import { ROLES } from '../../../utils/constant/global';
// import { formatterTime } from '../../../utils/handler';
import { formatTimestampInTimezone } from '../../../utils/timedoctorAttendanceHelpers';
import {
  buildAttendanceTimelineSegments,
  computeShiftRemainingMs,
  durationToMs as timelineDurationToMs,
  intervalsToTimelineWork,
  layoutShiftBarSegments,
  sumIntervalsMs
} from '../../../utils/attendanceTimelineHelpers';

import EmptyData from '../../../components/UI/EmptyData';

import './EmployeeDayTimelineCard.scss';

const toYMD = (d) => {
  const t = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const yyyy = String(t.getFullYear());
  const mm = String(t.getMonth() + 1).padStart(2, '0');
  const dd = String(t.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const startOfWeekSunday = (dateLike) => {
  const base = dateLike ? new Date(dateLike) : new Date();
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  d.setDate(d.getDate() - d.getDay()); // Sunday
  return d;
};

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const ms = (x) => {
  const n = Number(x);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const clamp01 = (n) => Math.max(0, Math.min(1, n));

const fmtHmsClock = (rawDuration) => {
  const msVal = timelineDurationToMs(rawDuration);
  if (!msVal) return '0:00:00';
  const totalSeconds = Math.floor(msVal / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
  const s = String(totalSeconds % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
};

const sumSegmentsMs = (segments = []) =>
  (Array.isArray(segments) ? segments : []).reduce(
    (acc, s) => acc + Math.max(0, (s?.end || 0) - (s?.start || 0)),
    0
  );

const fmtHmsParts = (milliseconds) => {
  const n = Math.max(0, Math.floor(Number(milliseconds || 0)));
  if (!Number.isFinite(n) || n <= 0) return '0s';
  const totalSeconds = Math.floor(n / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const parts = [];
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  if (s) parts.push(`${s}s`);
  return parts.length ? parts.join(' ') : '0s';
};

const SmileyIcon = ({ className }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <circle cx="9" cy="10" r="1.25" fill="currentColor" />
    <circle cx="15" cy="10" r="1.25" fill="currentColor" />
    <path
      d="M8.5 14.25C9.6 15.7 10.75 16.4 12 16.4C13.25 16.4 14.4 15.7 15.5 14.25"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// eslint-disable-next-line no-unused-vars
const TortoiseIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path
      fill="currentColor"
      d="M10.997 5.998a6.14 6.14 0 0 1 5.8 4.126l.075.233l.044.144h2.33a2.75 2.75 0 0 1 2.744 2.582l.005.167v1a1.75 1.75 0 0 1-1.606 1.743l-.143.005H18.62l.241.584a1.75 1.75 0 0 1-.813 2.22l-.137.064a1.8 1.8 0 0 1-.496.124l-.171.008h-1.787a1.75 1.75 0 0 1-1.51-.867l-.072-.137l-.539-1.143l.054-.007c-1.4.186-2.817.208-4.221.066l-.497-.057l-.535 1.136a1.75 1.75 0 0 1-1.583 1.005H4.75a1.75 1.75 0 0 1-1.618-2.415l.433-1.05a3.24 3.24 0 0 1-1.57-2.78a.75.75 0 0 1 .648-.742L2.745 12h1.88l.497-1.643a6.14 6.14 0 0 1 5.875-4.359m6.777 9.693q-1.158.465-2.356.765l-.549.129l.362.77a.25.25 0 0 0 .117.119l.053.018l.056.007h1.787a.25.25 0 0 0 .248-.28l-.017-.065l-.478-1.156h-.043l.411-.148zm-13.552 0l.39.152l.388.141l-.482 1.166a.25.25 0 0 0 .232.345h1.804l.057-.007a.25.25 0 0 0 .17-.137l.359-.763l.044.01a18 18 0 0 1-2.962-.906m6.775-8.194a4.64 4.64 0 0 0-4.371 3.087l-.068.207l-1.136 3.75l.163.059a16.67 16.67 0 0 0 10.42.133l.406-.133l.162-.059l-1.136-3.75a4.64 4.64 0 0 0-4.006-3.273l-.216-.016zm-6.977 6.5l.151-.5l-.507.002l.025.052q.13.25.33.446M17.37 12l.756 2.498l2.12.001a.25.25 0 0 0 .243-.192l.007-.058v-.999a1.25 1.25 0 0 0-1.122-1.243l-.128-.006z"
    />
  </svg>
);

const SHIFT_8H_MS = 8 * 60 * 60 * 1000;

const buildTimeline = ({ checkInTime, checkOutTime, breakSessions, fixedSpanMs }) => {
  const start = ms(checkInTime);
  const fixed = Number(fixedSpanMs);
  const end =
    start && Number.isFinite(fixed) && fixed > 0 ? start + fixed : ms(checkOutTime) || Date.now();
  if (!start || end <= start) return null;

  const breaks = (breakSessions || [])
    .map((b) => ({ in: ms(b?.breakIn), out: ms(b?.breakOut) }))
    .filter((b) => b.in && b.out && b.out > b.in)
    .sort((a, b) => a.in - b.in);

  const span = end - start;

  const segments = [];
  let cursor = start;
  breaks.forEach((b) => {
    const bi = Math.max(start, Math.min(end, b.in));
    const bo = Math.max(start, Math.min(end, b.out));
    if (bi > cursor) {
      segments.push({ type: 'work', start: cursor, end: bi });
    }
    if (bo > bi) {
      segments.push({ type: 'break', start: bi, end: bo });
    }
    cursor = Math.max(cursor, bo);
  });
  if (end > cursor) segments.push({ type: 'work', start: cursor, end });

  const withPos = segments.map((s) => {
    const left = clamp01((s.start - start) / span);
    const right = clamp01((s.end - start) / span);
    return { ...s, left, width: Math.max(0, right - left) };
  });

  return { start, end, span, segments: withPos, breaksCount: breaks.length };
};

const buildActiveFillSegments = (timeline, activeMilliseconds) => {
  if (!timeline) return null;
  const totalActive = Math.max(
    0,
    Math.min(Number(activeMilliseconds || 0), Number(timeline.span || 0))
  );
  const workSegments = timeline.segments.filter((s) => s.type === 'work');
  const breakSegments = timeline.segments.filter((s) => s.type === 'break');

  const filled = [];
  let remaining = totalActive;

  // Walk work segments left-to-right and fill up to remaining duration.
  workSegments.forEach((s) => {
    const segMs = s.end - s.start;
    if (segMs <= 0) return;
    const take = Math.max(0, Math.min(segMs, remaining));
    if (take > 0) {
      const end = s.start + take;
      const span = timeline.end - timeline.start;
      const left = clamp01((s.start - timeline.start) / span);
      const right = clamp01((end - timeline.start) / span);
      filled.push({ type: 'active', start: s.start, end, left, width: Math.max(0, right - left) });
      remaining -= take;
    }
  });

  // Add breaks (so it visually matches the main bar gaps)
  const breaks = breakSegments.map((b) => ({ ...b, type: 'break' }));

  return { filled, breaks };
};

const OBS_TIMELINE_OPTS = {
  includeLeadingBreaks: false,
  includeTrailingBreaks: false,
  minGapMs: 60 * 1000,
  minBreakWidthPct: 0.004
};

const timelineSegmentsToFill = (segs = []) => {
  const filled = [];
  const breaks = [];
  segs.forEach((s) => {
    const seg = {
      start: s.startMs,
      end: s.endMs,
      left: s.left,
      width: s.width
    };
    if (s.kind === 'break') breaks.push({ type: 'break', ...seg });
    else filled.push({ type: 'active', ...seg });
  });
  return { filled, breaks };
};

const buildObsFillFromIntervals = (timeline, intervals, now = Date.now()) => {
  if (!timeline) return null;
  const workIntervals = intervalsToTimelineWork(intervals, now);
  if (!workIntervals.length) return { filled: [], breaks: [] };

  const segs = buildAttendanceTimelineSegments(
    timeline.start,
    timeline.end,
    workIntervals.map((w) => ({
      timelogs: { start: w.startMs, end: w.isRunning ? undefined : w.endMs }
    })),
    now,
    undefined,
    OBS_TIMELINE_OPTS
  );
  return timelineSegmentsToFill(segs);
};

const toShiftBarBaseSegs = ({ filled = [], breaks = [] }) => [
  ...filled.map((s) => ({ kind: 'work', ...s })),
  ...breaks.map((s) => ({ kind: 'break', ...s }))
];

const fromShiftBarSegs = (segs = []) => {
  const filled = [];
  const breaks = [];
  const pending = [];
  segs.forEach((s) => {
    if (s.kind === 'pending') {
      pending.push(s);
      return;
    }
    const seg = { start: s.start, end: s.end, left: s.left, width: s.width };
    if (s.kind === 'break') breaks.push({ type: 'break', ...seg });
    else filled.push({ type: 'active', ...seg });
  });
  return { filled, breaks, pending };
};

/** Compress work/break into left portion; reserve right edge for remaining shift time. */
const layoutFillWithShiftPending = (fill, workedMs) => {
  if (!fill) return null;
  const pendingMs = computeShiftRemainingMs(timelineDurationToMs(workedMs), SHIFT_8H_MS);
  const scaled = layoutShiftBarSegments(toShiftBarBaseSegs(fill), pendingMs, SHIFT_8H_MS);
  return { ...fromShiftBarSegs(scaled), pendingMs };
};

const pairBreaks = (breakIns, breakOuts) => {
  const ins = Array.isArray(breakIns) ? breakIns : [];
  const outs = Array.isArray(breakOuts) ? breakOuts : [];
  const len = Math.min(ins.length, outs.length);
  const sessions = [];
  for (let i = 0; i < len; i += 1) {
    const bi = ms(ins[i]);
    const bo = ms(outs[i]);
    if (bi && bo && bo > bi) sessions.push({ breakIn: bi, breakOut: bo });
  }
  return sessions;
};

function EmployeeDayTimelineCard({ roleName }) {
  const { data: isLoggedInData } = useIsLoggedInQuery();
  const userId = isLoggedInData?.data?._id;
  const timeZone = useSelector(selectDisplayTimezone) || 'Asia/Kolkata';

  const fmtShort = (ts) => (ts ? formatTimestampInTimezone(ts, timeZone, false) : '-');
  const fmtHour = (ts) => (ts ? formatTimestampInTimezone(ts, timeZone, false) : '-');

  const todayYmd = useMemo(() => toYMD(new Date()), []);
  const [selectedDate, setSelectedDate] = useState(() => toYMD(new Date()));
  const [weekAnchor, setWeekAnchor] = useState(() => toYMD(new Date()));

  const dayStartMs = useMemo(
    () => (selectedDate ? moment.utc(selectedDate).startOf('day').valueOf() : null),
    [selectedDate]
  );
  const dayEndMs = useMemo(
    () => (selectedDate ? moment.utc(selectedDate).endOf('day').valueOf() : null),
    [selectedDate]
  );

  const isAdminOrHr = roleName === ROLES.ADMIN || roleName === ROLES.HR;
  const shouldFetch = !isAdminOrHr && userId && dayStartMs && dayEndMs;

  const fromIso = useMemo(
    () => (dayStartMs ? new Date(dayStartMs).toISOString() : null),
    [dayStartMs]
  );
  const toIso = useMemo(() => (dayEndMs ? new Date(dayEndMs).toISOString() : null), [dayEndMs]);

  const {
    data: chartData,
    isLoading: chartLoading,
    isFetching: chartFetching,
    refetch: refetchChartData
  } = useGetDashboardChartQuery(
    shouldFetch && fromIso && toIso ? { from: fromIso, to: toIso, userId } : skipToken,
    { refetchOnMountOrArgChange: true, refetchOnFocus: true }
  );

  useEffect(() => {
    if (shouldFetch) refetchChartData();
  }, [shouldFetch, selectedDate, refetchChartData]);

  const hrxRow = useMemo(() => {
    const arr = chartData?.data?.hrxTimeLogChart;
    return Array.isArray(arr) && arr.length ? arr[0] : null;
  }, [chartData]);

  const observantKey = useMemo(
    () => (selectedDate ? moment.utc(selectedDate, 'YYYY-MM-DD').format('DD-MMM-YYYY') : ''),
    [selectedDate]
  );

  const observantRow = useMemo(() => {
    const obj = chartData?.data?.observantTimeLogChart;
    if (!obj || typeof obj !== 'object') return null;
    return obj?.[observantKey] || null;
  }, [chartData, observantKey]);

  const observantIntervals = useMemo(() => {
    const arr = observantRow?.data;
    return Array.isArray(arr) ? arr : [];
  }, [observantRow]);

  const hasObsData = useMemo(() => {
    const total = Number(observantRow?.totalLog || 0);
    return (
      (Array.isArray(observantIntervals) && observantIntervals.length > 0) ||
      (Number.isFinite(total) && total > 0)
    );
  }, [observantIntervals, observantRow]);

  const hasHrxData = useMemo(() => {
    if (!hrxRow) return false;
    const inMs = ms(hrxRow?.checkInTime);
    const workMs = ms(hrxRow?.totalWorkMilliseconds ?? hrxRow?.totalWorkedMilliseconds);
    return Boolean(inMs || workMs);
  }, [hrxRow]);

  const session = useMemo(() => {
    if (hrxRow || observantRow) {
      return {
        checkInTime: hrxRow?.checkInTime ?? observantRow?.startTime,
        checkOutTime: hrxRow?.checkOutTime ?? observantRow?.endTime,
        totalBreakMilliseconds: hrxRow?.totalBreakMilliseconds ?? 0,
        totalWorkMilliseconds:
          hrxRow?.totalWorkMilliseconds ?? hrxRow?.totalWorkedMilliseconds ?? 0,
        hrxMilliseconds: hrxRow?.totalWorkMilliseconds ?? hrxRow?.totalWorkedMilliseconds ?? 0,
        observantMilliseconds: observantRow?.totalLog,
        breakSessions: pairBreaks(hrxRow?.lastBreakIn, hrxRow?.lastBreakOut)
      };
    }
    return null;
  }, [hrxRow, observantRow, selectedDate]);

  const checkInTime = session?.checkInTime;
  const checkOutTime = session?.checkOutTime;
  // const totalWorkMilliseconds = session?.totalWorkMilliseconds ?? session?.totalWorkedMilliseconds;
  // const totalBreakMilliseconds = session?.totalBreakMilliseconds;
  const hrxMilliseconds = session?.hrxMilliseconds;
  const observantMilliseconds = session?.observantMilliseconds;
  const observantClockMs = useMemo(() => {
    const fromLog = timelineDurationToMs(observantMilliseconds);
    const fromIntervals = observantIntervals.length ? sumIntervalsMs(observantIntervals) : 0;
    return Math.max(fromLog, fromIntervals);
  }, [observantMilliseconds, observantIntervals]);
  const breakSessions = session?.breakSessions || [];

  const timeline = useMemo(
    () =>
      buildTimeline({
        checkInTime,
        checkOutTime,
        breakSessions,
        fixedSpanMs: SHIFT_8H_MS
      }),
    [checkInTime, checkOutTime, breakSessions]
  );

  const rangeLabel = useMemo(() => {
    if (!timeline) return '';
    return `(${fmtHour(timeline.start)} - ${fmtHour(timeline.end)})`;
  }, [timeline]);

  if (isAdminOrHr) return null;

  const loading = chartLoading || chartFetching;
  const isToday = useMemo(() => selectedDate === toYMD(new Date()), [selectedDate]);

  const punctuality = useMemo(() => {
    const inMs = ms(checkInTime);
    if (!inMs || !selectedDate) return null;
    const scheduled = moment(selectedDate, 'YYYY-MM-DD')
      .hour(10)
      .minute(15)
      .second(0)
      .millisecond(0)
      .valueOf();
    const diff = inMs - scheduled;
    if (diff > 0) {
      return { status: 'late', diffMs: diff };
    }
    return { status: 'on-time', diffMs: Math.abs(diff) };
  }, [checkInTime, selectedDate]);

  const weekStart = useMemo(() => startOfWeekSunday(weekAnchor), [weekAnchor]);
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );
  const dowLabels = useMemo(() => ['S', 'M', 'T', 'W', 'T', 'F', 'S'], []);
  const shiftWeek = (deltaWeeks) => {
    const ws = startOfWeekSunday(weekAnchor);
    const next = addDays(ws, deltaWeeks * 7);
    const maxWeekStart = startOfWeekSunday(todayYmd);
    const clamped = next > maxWeekStart ? maxWeekStart : next;
    const nextYmd = toYMD(clamped);
    setWeekAnchor(nextYmd);
    // keep selected date inside the visible week
    const sel = selectedDate ? new Date(selectedDate) : new Date();
    const selStart = startOfWeekSunday(sel);
    if (toYMD(selStart) !== nextYmd) {
      setSelectedDate(nextYmd);
    }
  };

  // const spanMs = timeline?.span || 0;
  const obsFillRaw = useMemo(() => {
    if (observantIntervals.length) {
      const fromIntervals = buildObsFillFromIntervals(timeline, observantIntervals);
      const hasVisibleWork = (fromIntervals?.filled || []).some((s) => Number(s.width) > 0.01);
      if (hasVisibleWork) return fromIntervals;
    }
    return buildActiveFillSegments(timeline, observantClockMs);
  }, [timeline, observantIntervals, observantClockMs]);
  const hrxFillRaw = useMemo(
    () => buildActiveFillSegments(timeline, hrxMilliseconds),
    [timeline, hrxMilliseconds]
  );
  const obsFill = useMemo(
    () => layoutFillWithShiftPending(obsFillRaw, observantClockMs),
    [obsFillRaw, observantClockMs]
  );
  const hrxFill = useMemo(
    () => layoutFillWithShiftPending(hrxFillRaw, hrxMilliseconds),
    [hrxFillRaw, hrxMilliseconds]
  );

  return (
    <section className="employee-day-timeline">
      <div className="employee-day-timeline__head">
        <div className="employee-day-timeline__top-row">
          <div className="employee-day-timeline__week-wrap">
            <button
              type="button"
              className="employee-day-timeline__week-nav"
              aria-label="Previous week"
              onClick={() => shiftWeek(-1)}
            >
              <i className="icofont icofont-rounded-left" aria-hidden />
            </button>

            <div className="employee-day-timeline__week" role="tablist" aria-label="Week days">
              {weekDays.map((d, i) => {
                const ymd = toYMD(d);
                const selected = ymd === selectedDate;
                const today = ymd === toYMD(new Date());
                const isFuture = ymd > todayYmd;
                return (
                  <button
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${ymd}-${i}`}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    disabled={isFuture}
                    className={`employee-day-timeline__dow ${selected ? 'is-selected' : ''} ${
                      today ? 'is-today' : ''
                    }`}
                    onClick={() => setSelectedDate(ymd)}
                    title={d.toLocaleDateString()}
                  >
                    <span className="employee-day-timeline__dow-letter">{dowLabels[i]}</span>
                    <span className="employee-day-timeline__dow-date">
                      {String(d.getDate()).padStart(2, '0')}
                    </span>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              className="employee-day-timeline__week-nav"
              aria-label="Next week"
              disabled={toYMD(weekStart) === toYMD(startOfWeekSunday(todayYmd))}
              onClick={() => shiftWeek(1)}
            >
              <i className="icofont icofont-rounded-right" aria-hidden />
            </button>
          </div>
          <div className="employee-day-timeline__top-actions">
            {punctuality && !loading ? (
              <div
                className={`employee-day-timeline__punctuality ${
                  punctuality.status === 'late' ? 'is-late' : 'is-on-time'
                }`}
                title={
                  punctuality.status === 'late'
                    ? `Late by ${fmtHmsParts(punctuality.diffMs)} (after 10:15 AM)`
                    : 'On time (before 10:15 AM)'
                }
              >
                {punctuality.status === 'late' ? (
                  <>
                    <span className="employee-day-timeline__punctuality-time">
                      {fmtHmsParts(punctuality.diffMs)}
                    </span>
                    <span className="employee-day-timeline__punctuality-label">Late</span>
                    <TortoiseIcon className="employee-day-timeline__punctuality-icon is-orange" />
                  </>
                ) : (
                  <>
                    <span className="employee-day-timeline__punctuality-label">On Time</span>
                    <SmileyIcon className="employee-day-timeline__punctuality-icon is-orange" />
                  </>
                )}
              </div>
            ) : null}
          </div>
          <div className="employee-day-timeline__filter">
            <input
              id="employee-day-date"
              type="date"
              className="form-control form-control-sm employee-day-timeline__date-input"
              value={selectedDate}
              max={todayYmd}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                const safe = v > todayYmd ? todayYmd : v;
                setSelectedDate(safe);
                setWeekAnchor(safe);
              }}
              aria-label="Select date"
            />
          </div>
        </div>

        <div className="employee-day-timeline__dayline">
          <div className="employee-day-timeline__daytitle">
            <span>{isToday ? 'Today' : moment(selectedDate).format('ddd, D MMM')}</span>
            {rangeLabel ? <span className="employee-day-timeline__range">{rangeLabel}</span> : null}
          </div>
          <div className="employee-day-timeline__daymeta" aria-label="Day totals">
            <span className="employee-day-timeline__daymeta-item">
              <span className="employee-day-timeline__daymeta-label">Obs break</span>
              <span className="employee-day-timeline__daymeta-value">
                {fmtHmsClock(sumSegmentsMs(obsFill?.breaks))}
              </span>
            </span>
            <span className="employee-day-timeline__daymeta-item">
              <span className="employee-day-timeline__daymeta-label">HRX break</span>
              <span className="employee-day-timeline__daymeta-value">
                {hasHrxData ? fmtHmsClock(sumSegmentsMs(hrxFill?.breaks)) : 'Not checked-in'}
              </span>
            </span>
          </div>
        </div>
      </div>

      <div className="employee-day-timeline__body">
        {loading ? (
          <div className="employee-day-timeline__loading">
            <ListSkeleton rows={4} avatar={false} />
          </div>
        ) : !timeline ? (
          <div className="employee-day-timeline__empty">
            <EmptyData />
          </div>
        ) : (
          <>
            <div className="employee-day-timeline__pair">
              {hasObsData ? (
                <div className="employee-day-timeline__pair-item employee-day-timeline__pair-item--obs">
                  <div className="employee-day-timeline__pair-head">
                    <span className="employee-day-timeline__pair-title">
                      <span className="employee-day-timeline__source-dot employee-day-timeline__source-dot--obs" />
                      Observant
                    </span>
                    <span className="employee-day-timeline__clock">
                      {fmtHmsClock(observantClockMs)}
                    </span>
                  </div>
                  <div
                    className="employee-day-timeline__bar employee-day-timeline__bar--secondary"
                    aria-label="Observant timeline"
                  >
                    {(obsFill?.filled || []).map((s, idx) => (
                      <div
                        // eslint-disable-next-line react/no-array-index-key
                        key={`obs-active-${idx}`}
                        className="employee-day-timeline__seg employee-day-timeline__seg--obs"
                        style={{ left: `${s.left * 100}%`, width: `${s.width * 100}%` }}
                        title={`Observant: ${fmtHmsParts(s.end - s.start)} (${fmtShort(s.start)} - ${fmtShort(s.end)})`}
                      />
                    ))}
                    {(obsFill?.breaks || []).map((b, idx) => (
                      <div
                        // eslint-disable-next-line react/no-array-index-key
                        key={`obs-break-${idx}`}
                        className="employee-day-timeline__seg employee-day-timeline__seg--break"
                        style={{ left: `${b.left * 100}%`, width: `${b.width * 100}%` }}
                        title={`Break: ${fmtHmsParts(b.end - b.start)} (${fmtShort(b.start)} - ${fmtShort(b.end)})`}
                      />
                    ))}
                    {(obsFill?.pending || []).map((p, idx) => (
                      <div
                        // eslint-disable-next-line react/no-array-index-key
                        key={`obs-pending-${idx}`}
                        className="employee-day-timeline__seg employee-day-timeline__seg--pending"
                        style={{ left: `${p.left * 100}%`, width: `${p.width * 100}%` }}
                        title={`Pending • ${fmtHmsClock(obsFill.pendingMs)}`}
                        aria-label={`Pending work ${fmtHmsClock(obsFill.pendingMs)}`}
                      />
                    ))}
                  </div>
                </div>
              ) : null}

              {hasHrxData ? (
                <div className="employee-day-timeline__pair-item employee-day-timeline__pair-item--hrx">
                  <div className="employee-day-timeline__pair-head">
                    <span className="employee-day-timeline__pair-title">
                      <span className="employee-day-timeline__source-dot employee-day-timeline__source-dot--hrx" />
                      HRX
                    </span>
                    <span className="employee-day-timeline__clock">
                      {fmtHmsClock(hrxMilliseconds)}
                    </span>
                  </div>
                  <div
                    className="employee-day-timeline__bar employee-day-timeline__bar--secondary"
                    aria-label="HRX timeline"
                  >
                    {(hrxFill?.filled || []).map((s, idx) => (
                      <div
                        // eslint-disable-next-line react/no-array-index-key
                        key={`hrx-active-${idx}`}
                        className="employee-day-timeline__seg employee-day-timeline__seg--hrx"
                        style={{ left: `${s.left * 100}%`, width: `${s.width * 100}%` }}
                        title={`HRX: ${fmtHmsParts(s.end - s.start)} (${fmtShort(s.start)} - ${fmtShort(s.end)})`}
                      />
                    ))}
                    {(hrxFill?.breaks || []).map((b, idx) => (
                      <div
                        // eslint-disable-next-line react/no-array-index-key
                        key={`hrx-break-${idx}`}
                        className="employee-day-timeline__seg employee-day-timeline__seg--break"
                        style={{ left: `${b.left * 100}%`, width: `${b.width * 100}%` }}
                        title={`Break: ${fmtHmsParts(b.end - b.start)} (${fmtShort(b.start)} - ${fmtShort(b.end)})`}
                      />
                    ))}
                    {(hrxFill?.pending || []).map((p, idx) => (
                      <div
                        // eslint-disable-next-line react/no-array-index-key
                        key={`hrx-pending-${idx}`}
                        className="employee-day-timeline__seg employee-day-timeline__seg--pending"
                        style={{ left: `${p.left * 100}%`, width: `${p.width * 100}%` }}
                        title={`Pending • ${fmtHmsClock(hrxFill.pendingMs)}`}
                        aria-label={`Pending work ${fmtHmsClock(hrxFill.pendingMs)}`}
                      />
                    ))}
                  </div>
                </div>
              ) : hasObsData ? (
                <div className="employee-day-timeline__pair-item employee-day-timeline__pair-item--hrx employee-day-timeline__pair-item--empty">
                  <div className="employee-day-timeline__pair-head">
                    <span className="employee-day-timeline__pair-title">
                      <span className="employee-day-timeline__source-dot employee-day-timeline__source-dot--hrx" />
                      HRX
                    </span>
                    <span className="employee-day-timeline__pair-value">
                      Not checked-in on HRX app
                    </span>
                  </div>
                </div>
              ) : null}
            </div>
            <div className="employee-day-timeline__legend" aria-label="Timeline key">
              <span>
                <i className="employee-day-timeline__legend-swatch employee-day-timeline__legend-swatch--obs" />
                Observant
              </span>
              <span>
                <i className="employee-day-timeline__legend-swatch employee-day-timeline__legend-swatch--hrx" />
                HRX
              </span>
              <span>
                <i className="employee-day-timeline__legend-swatch employee-day-timeline__legend-swatch--break" />
                Break
              </span>
              <span>
                <i className="employee-day-timeline__legend-swatch employee-day-timeline__legend-swatch--pending" />
                Pending
              </span>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default EmployeeDayTimelineCard;
